import React, { Component } from 'react';
import { connect } from 'react-redux';
import { GoogleMap, Marker, withGoogleMap } from 'react-google-maps';
import MarkerClusterer from 'react-google-maps/lib/components/addons/MarkerClusterer';
import { onFetchGmapData } from '@uhe_actions/monitoring/UHEActions';
import { MAP_DEFAULT_CENTER } from '@constants/UHESettings';
import PropTypes, { func } from 'prop-types';

const MarkerClustererGoogleMap = withGoogleMap((props) => (
  <GoogleMap
    defaultZoom={props.zoom}
    defaultCenter={props.mapCenter}
    onIdle={props.onZoomBoundsChanged}
    onTilesLoaded={props.onTilesLoaded}
    ref={props.onMapMounted}
    options={props.mapOptions}
  >
    <MarkerClusterer
      averageCenter
      enableRetinaIcons
      gridSize={60}
      styles={props.clusterStyles}
      calculator={props.clusterRenderer}
    >
      {props.markers.filter((marker) => marker.latitude && marker.longitude)
        .map((marker, index) => {
          const componentKey = `marker_${index}`;
          return (
            <Marker
              position={{ lat: marker.latitude, lng: marker.longitude }}
              key={componentKey}
              icon={`/marker_${marker.status}.png`}
            />
          );
        })}
    </MarkerClusterer>
  </GoogleMap>
));

/**
 * Renders UHE Marker Clusterer
 */
class UHEMarkerClusterer extends Component {
  /**
   * Calculates the center point for the map from the markers list
   * @param {Array<Object>} markers markers
   * @returns {object} mapCenter & mapBounds
   */
  static calculateMapBoundsByMarkers(markers) {
    if (!markers || !Array.isArray(markers) || markers.length === 0) {
      return {
        mapCenter: MAP_DEFAULT_CENTER,
        mapBounds: null,
      };
    }

    const latitudes = markers.map((marker) => marker.latitude);
    const longitudes = markers.map((marker) => marker.longitude);

    const minLatitude = Math.min.apply(null, latitudes);
    const maxLatitude = Math.max.apply(null, latitudes);

    const minLongitude = Math.min.apply(null, longitudes);
    const maxLongitude = Math.max.apply(null, longitudes);

    const mapCenter = {
      lat: ((maxLatitude + minLatitude) / 2.0),
      lng: ((maxLongitude + minLongitude) / 2.0),
    };

    const mapBounds = {
      // bottom left
      south: minLatitude,
      west: minLongitude,
      // top right
      north: maxLatitude,
      east: maxLongitude,
    };

    return {
      mapCenter,
      mapBounds,
    };
  }

  /**
   * UHEMarkerClusterer Constructor
   * @param {object} props UHEMarkerClusterer Constructor Props
   * @return {void}
   */
  constructor(props) {
    super(props);

    this.tilesLoaded = false;
    this.mapRef = undefined;
    this.onMapMounted = this.onMapMounted.bind(this);
    this.onZoomBoundsChanged = this.onZoomBoundsChanged.bind(this);
    this.onTilesLoaded = this.onTilesLoaded.bind(this);
  }

  /**
   * On Tiles Loaded
   * @return {void}
   */
  onTilesLoaded() {
    this.tilesLoaded = true;
  }

  /**
   * Handles map zoom and boundaries change event
   * @returns {void}
   */
  onZoomBoundsChanged() {
    const { onZoomBoundsChanged } = this.props;
    const zoom = this.mapRef.getZoom();
    const bounds = this.mapRef.getBounds();
    const center = this.mapRef.getCenter();

    if (onZoomBoundsChanged && this.tilesLoaded) {
      onZoomBoundsChanged(zoom, bounds, center);
    }
  }

  /**
   * Handles map mounted event
   * @param {object} map map
   * @return {void}
   */
  onMapMounted(map) {
    this.mapRef = map;

    const { zoom, mapCenter: propsMapCenter, data } = this.props;
    if (this.mapRef && (!propsMapCenter || !zoom)) {
      const { mapCenter, mapBounds } = UHEMarkerClusterer.calculateMapBoundsByMarkers(data.list);
      this.mapRef.center = mapCenter;
      if (mapBounds) {
        this.mapRef.fitBounds(mapBounds);
      }
    }
  }

  /**
   * Renders the component
   * @return {JSX} React Element
   */
  render() {
    const {
      data, zoom, mapCenter, mapOptions, clusterStyles, clusterRenderer,
    } = this.props;
    const markers = data.list || [];
    return (
      <MarkerClustererGoogleMap
        loadingElement={<div style={{ height: '100%' }} />}
        containerElement={<div style={{ height: '237px' }} />}
        mapElement={<div style={{ height: '100%' }} />}
        markers={markers}
        onZoomBoundsChanged={this.onZoomBoundsChanged}
        onMapMounted={this.onMapMounted}
        zoom={zoom}
        mapCenter={mapCenter}
        onTilesLoaded={this.onTilesLoaded}
        mapOptions={mapOptions}
        clusterStyles={clusterStyles}
        clusterRenderer={clusterRenderer}
      />
    );
  }
}

/**
 * Selectes the part of the data from the store that the connected component needs.
 * @param {object} UheGmap Uhe Gmap
 * @return {object} data - return an object of data this component needs
 */
const mapStateToProps = ({ UheGmap = {} }) => {
  const { data = [] } = UheGmap;
  return { data };
};

UHEMarkerClusterer.defaultProps = {
  onZoomBoundsChanged: () => {},
  data: {},
  zoom: null,
  mapCenter: {},
  mapOptions: {},
  clusterStyles: [],
  clusterRenderer: func,
};

UHEMarkerClusterer.propTypes = {
  onZoomBoundsChanged: PropTypes.func,
  data: PropTypes.shape({
    list: PropTypes.arrayOf(PropTypes.shape({
      device_id: PropTypes.number,
      organization_id: PropTypes.number,
      organization_name: PropTypes.string,
      customer_id: PropTypes.number,
      customer_name: PropTypes.string,
      facility_id: PropTypes.number,
      facility_name: PropTypes.string,
      facility_country: PropTypes.string,
      facility_city: PropTypes.string,
      facility_addr: PropTypes.string,
      unit_id: PropTypes.number,
      unit_name: PropTypes.string,
      cart_name: PropTypes.string,
      acknowledged_by: PropTypes.string,
      acknowledged_notes: PropTypes.string,
      notes: PropTypes.string,
      is_live: PropTypes.number,
      customer_auto_notification: PropTypes.bool,
      device_config_mode: PropTypes.string,
      machine_parent_part_serial_number: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      status: PropTypes.string,
    })),
    page: PropTypes.shape({
      size: PropTypes.number,
      number: PropTypes.number,
      totalElements: PropTypes.number,
      totalPages: PropTypes.number,
    }),
  }),
  zoom: PropTypes.number,
  mapCenter: PropTypes.shape({
    lat: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.func,
    ]),
    lng: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.func,
    ]),
  }),
  mapOptions: PropTypes.shape({
    controlSize: PropTypes.number,
    fullscreenControl: PropTypes.bool,
    streetViewControl: PropTypes.bool,

  }),
  clusterStyles: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    anchorIcon: PropTypes.arrayOf,
    anchorText: PropTypes.arrayOf,
    textSize: PropTypes.number,
  })),
  clusterRenderer: PropTypes.func,
};

export default connect(mapStateToProps, {
  onFetchGmapData,
})(UHEMarkerClusterer);
